import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Bike, Edit2, Eye, Trash2, Package } from 'lucide-react';
import { equipmentAPI, getImageUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const CATEGORY_LABELS = {
  bikes: '🚲 Bikes', ski_gear: '⛷ Ski Gear', water_sports: '🏄 Water Sports',
  hiking: '🥾 Hiking', camping: '⛺ Camping', winter_sports: '🏔 Winter Sports',
  beach_gear: '🏖 Beach Gear', photography: '📷 Photography', vehicles: '🚗 Vehicles', other: '📦 Other'
};

const EquipmentPage = () => {
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  const fetchEquipment = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 16 };
      if (search) params.search = search;
      if (category) params.category = category;
      if (availableOnly) params.available = 'true';
      const res = await equipmentAPI.getAll(params);
      setItems(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      toast.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEquipment(); }, [search, category, availableOnly]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await equipmentAPI.delete(id);
      toast.success('Equipment deleted');
      fetchEquipment();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Equipment Catalog</h1>
          <p className="page-subtitle">{pagination.total} items in inventory</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => navigate('/equipment/bookings')}>
            <Package size={16} /> Bookings
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/equipment/operations')}>
            ⚙ Operations
          </button>
          {canManage && (
            <button className="btn btn-primary" onClick={() => navigate('/equipment/new')}>
              <Plus size={16} /> Add Equipment
            </button>
          )}
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search equipment..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 180 }} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={availableOnly} onChange={e => setAvailableOnly(e.target.checked)} />
          Available only
        </label>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <Bike size={48} />
          <h3>No equipment found</h3>
          <p>Add equipment to your rental catalog</p>
          {canManage && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/equipment/new')}>
              <Plus size={16} /> Add Equipment
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 18 }}>
          {items.map(item => {
            const availPct = item.stock.total > 0 ? (item.stock.available / item.stock.total) * 100 : 0;
            return (
              <div key={item._id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Image */}
                <div style={{ height: 160, background: '#F3F4F6', position: 'relative', overflow: 'hidden' }}>
                  {item.images?.[0]?.url ? (
                    <img src={getImageUrl(item.images[0].url)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 40 }}>
                      {CATEGORY_LABELS[item.category]?.split(' ')[0] || '📦'}
                    </div>
                  )}
                  {item.isFeatured && (
                    <span className="badge badge-yellow" style={{ position: 'absolute', top: 10, left: 10 }}>Featured</span>
                  )}
                  <span className={`badge ${item.stock.available > 0 ? 'badge-green' : 'badge-red'}`}
                    style={{ position: 'absolute', top: 10, right: 10 }}>
                    {item.stock.available > 0 ? `${item.stock.available} avail` : 'Out of stock'}
                  </span>
                </div>

                <div style={{ padding: 14 }}>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>
                    {CATEGORY_LABELS[item.category] || item.category}
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: '#111827' }}>{item.name}</h3>

                  {/* Stock bar */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                      <span>Stock</span>
                      <span>{item.stock.available}/{item.stock.total}</span>
                    </div>
                    <div style={{ height: 4, background: '#E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${availPct}%`, height: '100%', background: availPct > 50 ? '#10B981' : availPct > 20 ? '#F59E0B' : '#EF4444', borderRadius: 2 }} />
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                        ${item.pricing?.dailyRate?.toLocaleString()}
                      </span>
                      <span style={{ fontSize: 12, color: '#9CA3AF' }}>/day</span>
                    </div>
                    <div className="table-actions">
                      <button className="btn btn-sm btn-ghost btn-icon" onClick={() => navigate(`/equipment/${item._id}`)}><Eye size={14} /></button>
                      {canManage && <button className="btn btn-sm btn-ghost btn-icon" onClick={() => navigate(`/equipment/${item._id}/edit`)}><Edit2 size={14} /></button>}
                      {canManage && <button className="btn btn-sm btn-ghost btn-icon" style={{ color: '#EF4444' }} onClick={() => handleDelete(item._id, item.name)}><Trash2 size={14} /></button>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`btn btn-sm ${pagination.page === p ? 'btn-primary' : 'btn-secondary'}`} onClick={() => fetchEquipment(p)}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EquipmentPage;
